import { ChevronLeft, ChevronRight } from "lucide-react"
import { useState } from "react"

import {
  Button,
  cn,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@atlant/ui"

import { formatAmount } from "#utils/money.ts"

import { headClass, SortableHead, type SortDir } from "../../components/sortable-head.tsx"
import type { LateInvoice } from "../late-invoices.tsx"

const PAGE_SIZE = 10
const LATE_THRESHOLD_DAYS = 30
const COLUMN_COUNT = 8

type SortKey = "number" | "vendor" | "serviceDate" | "invoiceDate" | "daysLate"

const compare = (a: LateInvoice, b: LateInvoice, key: SortKey) => {
  switch (key) {
    case "daysLate":
      return a.daysLate - b.daysLate
    case "serviceDate":
    case "invoiceDate":
      // ISO plain dates sort lexicographically
      return a[key].plaindate.localeCompare(b[key].plaindate)
    default:
      return a[key].localeCompare(b[key])
  }
}

// First, last and current ±1 page; null marks a gap
const pageRange = (current: number, count: number) =>
  Array.from({ length: count }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === count || Math.abs(p - current) <= 1)
    .flatMap((p, i, pages) => (i > 0 && p - pages[i - 1]! > 1 ? [null, p] : [p]))

interface LateInvoicesTableProps {
  invoices: LateInvoice[]
  isLoading: boolean
  tails: Map<string, string>
  search: string
  emptyText: string
}

export const LateInvoicesTable = ({
  invoices,
  isLoading,
  tails,
  search,
  emptyText,
}: LateInvoicesTableProps) => {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({
    key: "daysLate",
    dir: "desc",
  })
  const [page, setPage] = useState(1)

  const term = search.trim().toLowerCase()
  const rows = invoices
    .filter(
      ({ vendor, number }) =>
        !term ||
        vendor.toLowerCase().includes(term) ||
        number.toLowerCase().includes(term),
    )
    .toSorted((a, b) => {
      const result = compare(a, b, sort.key)
      return (sort.dir === "asc" ? result : -result) || a.number.localeCompare(b.number)
    })

  const pageCount = Math.max(Math.ceil(rows.length / PAGE_SIZE), 1)
  // Clamp after the data, aircraft, year or search shrinks the result
  const currentPage = Math.min(page, pageCount)
  const from = (currentPage - 1) * PAGE_SIZE
  const pageRows = rows.slice(from, from + PAGE_SIZE)

  const onSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    )
    setPage(1)
  }

  const sortable = (key: SortKey, label: string, className?: string) => (
    <SortableHead
      label={label}
      active={sort.key === key}
      dir={sort.dir}
      onSort={() => onSort(key)}
      className={className}
    />
  )

  return (
    <div className="bg-card flex flex-col rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            {sortable("number", "PUR no")}
            {sortable("vendor", "Vendor", "w-full")}
            {sortable("serviceDate", "Service date")}
            {sortable("invoiceDate", "Invoice date")}
            {sortable("daysLate", "Days late", "bg-muted text-center")}
            <TableHead className={headClass}>Aircraft</TableHead>
            <TableHead className={`${headClass} text-right`}>Amount, €</TableHead>
            <TableHead className={headClass}>Owner</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading &&
            Array.from({ length: 6 }, (_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Skeleton className="h-5 w-full" />
                </TableCell>
              </TableRow>
            ))}

          {!isLoading && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={COLUMN_COUNT}
                className="text-muted-foreground py-8 text-center"
              >
                {term ? "No invoices match your search" : emptyText}
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            pageRows.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="whitespace-nowrap">{invoice.number}</TableCell>
                <TableCell className="max-w-60 truncate">{invoice.vendor}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {invoice.serviceDate.displaydate}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {invoice.invoiceDate.displaydate}
                </TableCell>
                <TableCell
                  className={cn(
                    "bg-muted/50 text-center font-semibold tabular-nums",
                    invoice.daysLate > LATE_THRESHOLD_DAYS && "text-destructive",
                  )}
                >
                  {invoice.daysLate}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {(invoice.aircraftId && tails.get(invoice.aircraftId)) ?? "—"}
                </TableCell>
                <TableCell className="text-right font-semibold whitespace-nowrap tabular-nums">
                  {formatAmount(
                    invoice.total,
                    invoice.currency === "EUR" ? undefined : invoice.currency,
                  )}
                </TableCell>
                {/* Owner approval is not modelled yet */}
                <TableCell />
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {!isLoading && rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
          <span className="text-muted-foreground text-sm">
            Showing {from + 1} to {from + pageRows.length} of {rows.length} documents
          </span>
          <nav aria-label="Pagination" className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setPage(currentPage - 1)}
            >
              <ChevronLeft />
              Previous
            </Button>
            {pageRange(currentPage, pageCount).map((p, i) =>
              p === null ? (
                <span key={`gap-${i}`} className="text-muted-foreground px-1 text-sm">
                  …
                </span>
              ) : (
                <Button
                  key={p}
                  variant={p === currentPage ? "outline" : "ghost"}
                  size="icon-sm"
                  aria-current={p === currentPage ? "page" : undefined}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              ),
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
              <ChevronRight />
            </Button>
          </nav>
        </div>
      )}
    </div>
  )
}
